import {FormShell} from '@/components';
import {BUYERS_MODAL_GUTTER_MARGIN_CLASS} from '@/features/buyers/constants';

import {BuyerDetailsSection} from '../create-buyer/BuyerDetailsSection';
import {BuyerFatcaCrsSupplementaryTabContent} from '../create-buyer/BuyerFatcaCrsSupplementaryTabContent';

import type {SubTab} from '../create-buyer/CreateBuyerTabContent';
import type {BuyerFormAi} from '@/features/buyers/hooks';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {FatcaCrsSupplementaryPayload} from '@sollapay/types';
import type {FC} from 'react';
import type {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayReplace,
  UseFormReturn
} from 'react-hook-form';

export interface AddBuyerToPurchaseTabContentProps {
  trustAccountId: string;
  form: UseFormReturn<CreateBuyerFormValues>;
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[];
  appendBuyer: UseFieldArrayAppend<CreateBuyerFormValues, 'buyers'>;
  removeBuyer: (index: number) => void;
  replaceBuyers: UseFieldArrayReplace<CreateBuyerFormValues, 'buyers'>;
  saveError?: unknown;
  onSupplementaryValuesChange: (values: FatcaCrsSupplementaryPayload | null) => void;
  activeSubTab: SubTab;
  ai: BuyerFormAi;
}

/**
 * Trimmed sibling of `CreateBuyerTabContent` for adding buyer(s) to an EXISTING purchase — no
 * purchase documents and no asset/purchase-price fields (the purchase already has both). AI reads
 * only each buyer row's own KYC documents.
 */
export const AddBuyerToPurchaseTabContent: FC<AddBuyerToPurchaseTabContentProps> = ({
  trustAccountId,
  form,
  buyerFields,
  appendBuyer,
  removeBuyer,
  replaceBuyers,
  saveError,
  onSupplementaryValuesChange,
  activeSubTab,
  ai
}) => {
  const {aiFields, isProcessing, rowAi, markFieldModified, reindexBuyerAiFields} = ai;

  return (
    <div className="flex flex-col">
      <div hidden={activeSubTab !== 'buyer-details'}>
        <FormShell
          errorClassName={BUYERS_MODAL_GUTTER_MARGIN_CLASS}
          id="add-buyer-to-purchase-form"
          onSubmit={e => e.preventDefault()}
          saveError={saveError}
        >
          <BuyerDetailsSection
            form={form}
            buyerFields={buyerFields}
            appendBuyer={appendBuyer}
            removeBuyer={removeBuyer}
            replaceBuyers={replaceBuyers}
            aiFields={aiFields}
            isProcessing={isProcessing}
            rowAi={rowAi}
            borders="none"
            markFieldModified={markFieldModified}
            reindexBuyerAiFields={reindexBuyerAiFields}
          />
        </FormShell>
      </div>

      <div hidden={activeSubTab !== 'fatca-crs'} className="flex flex-col gap-6 py-4">
        <BuyerFatcaCrsSupplementaryTabContent
          trustAccountId={trustAccountId}
          buyerForm={form}
          saveError={saveError}
          onSupplementaryValuesChange={onSupplementaryValuesChange}
        />
      </div>
    </div>
  );
};
